import { useState, useEffect, useRef, createContext, useContext, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);

  const exchangeCalledRef = useRef(false);

  useEffect(() => {
    // Check for stancl impersonation token exchange
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('impersonate');
    const tenantCode       = params.get('tenant');

    if (impersonateToken && tenantCode) {
      // Guard against React StrictMode double-invoke
      if (exchangeCalledRef.current) return;
      exchangeCalledRef.current = true;

      fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/impersonate/exchange?token=${impersonateToken}`, {
        headers: { 'X-Tenant': tenantCode, 'Accept': 'application/json' },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.token) {
            sessionStorage.setItem('token',            data.token);
            sessionStorage.setItem('user',             JSON.stringify(data.user));
            sessionStorage.setItem('permissions',      JSON.stringify(data.permissions || {}));
            sessionStorage.setItem('token_expires_at', data.expires_at || '');
            sessionStorage.setItem('tenant_id',        data.tenant_id);
            sessionStorage.setItem('impersonation_session', JSON.stringify({
              school_name: data.school_name,
              tenant_code: data.tenant_id,
            }));
            // Set state directly so ProtectedRoute sees the user immediately
            setToken(data.token);
            setUser(data.user);
            setPermissions(data.permissions || {});
          }
          window.history.replaceState({}, '', '/');
          setLoading(false);
        })
        .catch(() => {
          window.history.replaceState({}, '', '/');
          setLoading(false);
        });
      return;
    }

    const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
    const storedUser  = sessionStorage.getItem('user')  || localStorage.getItem('user');
    const storedPermissions = sessionStorage.getItem('permissions') || localStorage.getItem('permissions');
    const expiresAt   = sessionStorage.getItem('token_expires_at') || localStorage.getItem('token_expires_at');

    if (storedToken && storedUser && storedUser !== 'undefined') {
      if (expiresAt && new Date(expiresAt) < new Date()) {
        clearAuth();
        setLoading(false);
        return;
      }

      authAPI.getUser().then((res) => {
        const freshUser = res?.data;
        if (freshUser) {
          const { permissions: perms, teacher_id: _, ...userData } = freshUser;
          setToken(storedToken);
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          if (perms) {
            setPermissions(perms);
            localStorage.setItem('permissions', JSON.stringify(perms));
          } else if (storedPermissions && storedPermissions !== 'undefined') {
            setPermissions(JSON.parse(storedPermissions));
          }
        } else {
          clearAuth();
        }
      }).catch((err) => {
        if (err.response?.status === 403 && err.response?.data?.suspended) {
          setSuspended(true);
        } else {
          clearAuth();
        }
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, authToken, userPermissions, expiresAt, tenantId) => {
    setUser(userData);
    setToken(authToken);
    setPermissions(userPermissions || {});
    const isImpersonationTab = !!sessionStorage.getItem('impersonation_session');
    const storage = isImpersonationTab ? sessionStorage : localStorage;
    storage.setItem('token', authToken);
    storage.setItem('user', JSON.stringify(userData));
    storage.setItem('permissions', JSON.stringify(userPermissions || {}));
    storage.setItem('token_expires_at', expiresAt);
    if (tenantId) storage.setItem('tenant_id', tenantId);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const updateUser = (userData) => {
    setUser({ ...userData });
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('tenant_id');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('permissions');
    sessionStorage.removeItem('token_expires_at');
    sessionStorage.removeItem('tenant_id');
    sessionStorage.removeItem('impersonation_session');
  };

  const hasPermission = useCallback((resource, action) => {
    return permissions[resource]?.includes(action) || false;
  }, [permissions]);

  const canRead = useCallback((resource) => hasPermission(resource, 'read'), [hasPermission]);
  const canCreate = useCallback((resource) => hasPermission(resource, 'create'), [hasPermission]);
  const canUpdate = useCallback((resource) => hasPermission(resource, 'update'), [hasPermission]);
  const canDelete = useCallback((resource) => hasPermission(resource, 'delete'), [hasPermission]);
  const canExport = useCallback((resource) => hasPermission(resource, 'export'), [hasPermission]);

  const refreshPermissions = useCallback(async (fresh) => {
    try {
      const perms = fresh ?? (await authAPI.getUser())?.data?.permissions;
      if (perms) {
        setPermissions(perms);
        localStorage.setItem('permissions', JSON.stringify(perms));
      }
    } catch {
      // silent
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, permissions, loading, suspended,
      login, logout, updateUser, refreshPermissions,
      hasPermission, canRead, canCreate, canUpdate, canDelete, canExport,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
